import { Autocomplete, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useContactsList, type Contact } from '@/models/contacts';

interface LocalContactPickerProps {
  label?: string;
  placeholder?: string;
  onContactSelect: (contact: Contact) => void;
}

export function LocalContactPicker({
  label,
  placeholder,
  onContactSelect,
}: LocalContactPickerProps) {
  const [search, setSearch] = useState('');
  const { data } = useContactsList({
    search: search.length >= 2 ? search : undefined,
    view: 'all',
    pageSize: 20,
  });

  const options = useMemo(() => {
    const rows = data?.rows ?? [];
    const map = new Map<string, { contact: Contact; label: string }>();
    for (const c of rows) {
      const fullName =
        [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || '';
      const lbl = `${fullName}${c.email ? ' — ' + c.email : ''}`;
      map.set(lbl, { contact: c, label: lbl });
    }
    return map;
  }, [data]);

  return (
    <Autocomplete
      label={label}
      placeholder={placeholder ?? 'Search local contacts...'}
      value={search}
      onChange={setSearch}
      data={Array.from(options.keys())}
      onOptionSubmit={(value) => {
        const opt = options.get(value);
        if (opt) {
          onContactSelect(opt.contact);
          setSearch(opt.label);
        }
      }}
      renderOption={({ option }) => <Text size="sm">{option.value}</Text>}
    />
  );
}
